type CardBadgeSemanticKind =
	| 'taxonomy'
	| 'access'
	| 'entity_type'
	| 'progress_state'
	| 'learning_mode'
	| 'utility'

export type CardContext =
	| 'home'
	| 'catalog'
	| 'related_course'
	| 'related_collection'
	| 'dashboard_grid'

export type ResolvedCardBadge = {
	id: string
	label: string
	semanticKind: CardBadgeSemanticKind
	priority: number
}

export type ResolvedCardMetaLine = {
	parts: string[]
}

export type ResolvedCardRelationship = {
	kind: 'parent_collection'
	label: string
	href?: string
	variant: 'subtle'
}

export type ResolvedCardPresentation = {
	badges: ResolvedCardBadge[]
	metaLine?: ResolvedCardMetaLine
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string

type CardPresentationEntity = {
	kind?: 'course' | 'collection' | null
	primarySubcategoryLabel?: string | null
	moduleCount?: number | null
	topicCount?: number | null
	childCourseCount?: number | null
	isSequential?: boolean | null
	accessLevel?: 'free' | 'subscription' | string | null
	typeLabel?: string | null
	statusLabel?: string | null
	learningModeLabel?: string | null
}

type RelationshipEntity = {
	parentCollections?: Array<{
		id: string
		title: string
		slug: string
		href?: string
	}> | null
}

function compactCountLabel(
	count: number | null | undefined,
	label: string,
): string | null {
	if (typeof count !== 'number' || Number.isNaN(count) || count < 0) return null
	return `${count} ${label}`
}

function stripCountPrefix(value: string, fallback: string): string {
	const cleaned = value.replace(/^\s*[\d.,]+\s*/u, '').trim()
	return cleaned || fallback
}

function getLearningModeLabel(params: {
	entity: CardPresentationEntity
	tr: TranslateFn
}) {
	if (params.entity.learningModeLabel) return params.entity.learningModeLabel
	if (!params.entity.isSequential) return null
	const translated = params.tr('common.studyPanel.badges.goalOriented')
	return translated && translated !== 'common.studyPanel.badges.goalOriented'
		? translated
		: 'Guiado'
}

export function resolveCourseCardPresentation(
	entity: CardPresentationEntity,
	context: CardContext,
	tr: TranslateFn,
): ResolvedCardPresentation {
	const badges: ResolvedCardBadge[] = []
	const subcategoryLabel = entity.primarySubcategoryLabel?.trim()
	const isCollection = entity.kind === 'collection'

	if (context === 'dashboard_grid') {
		if (entity.typeLabel) {
			badges.push({
				id: 'entity-type',
				label: entity.typeLabel,
				semanticKind: 'entity_type',
				priority: 1,
			})
		}
		if (entity.statusLabel) {
			badges.push({
				id: 'progress-state',
				label: entity.statusLabel,
				semanticKind: 'progress_state',
				priority: 2,
			})
		}
		if (isCollection && entity.isSequential) {
			const learningModeLabel = getLearningModeLabel({ entity, tr })
			if (learningModeLabel) {
				badges.push({
					id: 'learning-mode',
					label: learningModeLabel,
					semanticKind: 'learning_mode',
					priority: 3,
				})
			}
		}

		return {
			badges,
		}
	}

	if (isCollection && entity.typeLabel) {
		badges.push({
			id: 'program-type',
			label: entity.typeLabel,
			semanticKind: 'entity_type',
			priority: 1,
		})
	}

	if (isCollection && entity.accessLevel === 'free') {
		const freeLabel = tr('marketing.footer.freeBadge')
		badges.push({
			id: 'access-free',
			label:
				freeLabel && freeLabel !== 'marketing.footer.freeBadge'
					? freeLabel
					: 'Gratis',
			semanticKind: 'access',
			priority: 2,
		})
	}

	if (subcategoryLabel && !isCollection) {
		badges.push({
			id: 'taxonomy',
			label: subcategoryLabel,
			semanticKind: 'taxonomy',
			priority: 1,
		})
	}

	if (context === 'catalog') {
		const metaParts = isCollection
			? [
					compactCountLabel(
						entity.childCourseCount,
						stripCountPrefix(
							tr('common.studyPanel.units.courses', {
								count: entity.childCourseCount ?? 0,
							}),
							'rutas',
						),
					),
				].filter((value): value is string => Boolean(value))
			: [
					compactCountLabel(
						entity.moduleCount,
						tr('columns.course.moduleCount', {
							defaultValue: 'Módulos',
						}),
					),
					compactCountLabel(
						entity.topicCount,
						tr('columns.course.topicCount', {
							defaultValue: 'Temas',
						}),
					),
				].filter((value): value is string => Boolean(value))

		return {
			badges,
			metaLine: metaParts.length > 0 ? { parts: metaParts } : undefined,
		}
	}

	return {
		badges,
	}
}

export function resolveCourseRelationshipContext(
	entity: RelationshipEntity,
	tr: TranslateFn,
): ResolvedCardRelationship | undefined {
	const parentCollections = entity.parentCollections ?? []
	if (parentCollections.length === 0) return undefined

	if (parentCollections.length === 1) {
		const [collection] = parentCollections
		if (!collection) return undefined
		return {
			kind: 'parent_collection',
			label: tr('courses.detail.relationship.single', {
				collection: collection.title,
			}),
			href: collection.href,
			variant: 'subtle',
		}
	}

	const firstCollection = parentCollections[0]
	return {
		kind: 'parent_collection',
		label: tr('courses.detail.relationship.multiple', {
			count: parentCollections.length,
			collection: firstCollection?.title ?? '',
		}),
		href: firstCollection?.href,
		variant: 'subtle',
	}
}
